"use client"

import { useMutation, useQueryClient } from "@tanstack/react-query"
import { type ComponentProps, useState } from "react"
import { LuMinus as MinusIcon, LuPlus as PlusIcon } from "react-icons/lu"
import { toast } from "sonner"
import { addCartItem } from "@/api/cart"
import { Button } from "@/components/ui/button"
import { cn } from "@/utilities"

interface ProductDetailsAddCartProps extends ComponentProps<"div"> {
  productId: number
  disabled?: boolean
}

const DIGITS_ONLY_REGEX = /^\d*$/

export function ProductDetailsAddCart({
  productId,
  disabled,
  className,
  ...props
}: ProductDetailsAddCartProps) {
  const queryClient = useQueryClient()
  const [quantity, setQuantity] = useState(1)
  const [errorMessage, setErrorMessage] = useState<string | null>(null)

  const maxLength = 3

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value

    const isOnlyDigits = DIGITS_ONLY_REGEX.test(value)

    if (!isOnlyDigits) return

    if (value.length <= maxLength) {
      setQuantity(value === "" ? 1 : Number.parseInt(value, 10))
    }
  }

  const handleIncrement = () => {
    setQuantity((quantity) => quantity + 1)
  }

  const handleDecrement = () => {
    setQuantity((quantity) => Math.max(1, quantity - 1))
  }

  const addToCartMutation = useMutation({
    mutationFn: addCartItem,
    onMutate: () => {
      setErrorMessage(null)

      const toastId = toast.loading("Adicionando produto ao carrinho...")

      return { toastId }
    },
    onError: (_error, _variables, context) => {
      setErrorMessage("Nao foi possivel adicionar o produto ao carrinho.")
      toast.error("Nao foi possivel adicionar o produto ao carrinho.", {
        id: context?.toastId,
      })
    },
    onSuccess: async (response, _variables, context) => {
      if (!response.ok) {
        const message = response.message?.trim()
          ? response.message
          : "Nao foi possivel adicionar o produto ao carrinho."
        setErrorMessage(message)
        toast.error(message, { id: context?.toastId })
        return
      }

      toast.success("Produto adicionado ao carrinho.", {
        id: context?.toastId,
      })
      await queryClient.invalidateQueries({ queryKey: ["cart", "items"] })
    },
  })

  const handleAdd = () => {
    setErrorMessage(null)

    addToCartMutation.mutate({
      productId,
      quantity,
    })
  }

  return (
    <div
      className={cn(
        "flex flex-wrap items-center gap-4 md:max-w-100",
        className
      )}
      {...props}
    >
      <div className='flex h-10 w-full items-stretch overflow-hidden rounded-md border border-gray-300 sm:flex-1'>
        <Button
          aria-hidden='true'
          aria-label='Diminuir quantidade'
          className='rounded-none border-0 px-3 text-gray-700 text-lg hover:bg-gray-100'
          disabled={disabled || addToCartMutation.isPending}
          onClick={handleDecrement}
          size='icon-lg'
          tabIndex={-1}
          type='button'
          variant='ghost'
        >
          <MinusIcon />
        </Button>

        <input
          aria-label='Quantidade selecionada'
          aria-live='polite'
          className='w-full border-gray-300 border-x bg-white text-center font-medium font-mono text-sm outline-none'
          disabled={disabled || addToCartMutation.isPending}
          onChange={handleInputChange}
          value={quantity}
        />

        <Button
          aria-hidden='true'
          aria-label='Aumentar quantidade'
          className='rounded-none border-0 px-3 text-gray-700 text-lg hover:bg-gray-100'
          disabled={disabled || addToCartMutation.isPending}
          onClick={handleIncrement}
          size='icon-lg'
          tabIndex={-1}
          type='button'
          variant='ghost'
        >
          <PlusIcon />
        </Button>
      </div>

      <Button
        className='flex-1 bg-blue-600 hover:bg-blue-700'
        disabled={disabled || addToCartMutation.isPending}
        onClick={handleAdd}
        size='lg'
        variant='default'
      >
        {addToCartMutation.isPending
          ? "Adicionando..."
          : "Adicionar ao carrinho"}
      </Button>

      {errorMessage ? (
        <p aria-live='polite' className='w-full text-destructive text-sm'>
          {errorMessage}
        </p>
      ) : null}
    </div>
  )
}
